import { Capacitor } from '@capacitor/core'

/**
 * Native-app glue. Every function is a no-op on the website, so the web build
 * and the Cloudflare deploy behave exactly as before.
 *
 * Plugins are imported lazily so the website never downloads their code.
 */

export const isNativeApp = (): boolean => Capacitor.isNativePlatform()
export const nativePlatform = (): 'ios' | 'android' | 'web' => Capacitor.getPlatform() as 'ios' | 'android' | 'web'

/** Event the Android hardware back button raises; App.tsx decides what it means. */
export const NATIVE_BACK_EVENT = 'pocketrealms:native-back'

let started = false

/** Call once at startup, before the first render. */
export async function startNativeShell(): Promise<void> {
  if (started || !isNativeApp()) return
  started = true

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    await StatusBar.setStyle({ style: Style.Dark })
    if (nativePlatform() === 'android') await StatusBar.setBackgroundColor({ color: '#0b1220' })
  } catch { /* status bar is cosmetic */ }

  try {
    const { App } = await import('@capacitor/app')
    // Android's back button. Without a handler the webview has no history in a
    // single-page game, so back simply CLOSED the app mid-session. App.tsx
    // listens for this event: off Home it goes Home, on Home it minimises.
    await App.addListener('backButton', () => {
      window.dispatchEvent(new CustomEvent(NATIVE_BACK_EVENT))
    })
  } catch { /* no app plugin, default behaviour */ }
}

/** Hide the native splash once the game has actually drawn something. */
export async function hideNativeSplash(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen')
    await SplashScreen.hide({ fadeOutDuration: 250 })
  } catch { /* already hidden */ }
}

/** Send the app to the background (Android back on the Home screen). */
export async function minimiseNativeApp(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const { App } = await import('@capacitor/app')
    await App.minimizeApp()
  } catch { /* not supported */ }
}
